import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "hooks";
import { getObjectOperationalHoursCorrectionsThunk } from "app/features";
import { OperationalHoursCorrection } from "app/data/helpers/operationalHoursHelpers";

export const useOperationalHoursHistory = (
  accountId: string,
  objectId?: string
) => {
  const dispatch = useAppDispatch();
  const requestId = useRef(0);
  const [corrections, setCorrections] = useState<OperationalHoursCorrection[]>(
    []
  );
  const [loading, setLoading] = useState(Boolean(accountId && objectId));

  const refresh = useCallback(async () => {
    if (!accountId || !objectId) {
      setCorrections([]);
      setLoading(false);
      return;
    }

    const currentRequest = ++requestId.current;
    setLoading(true);

    try {
      const rows = await dispatch(
        getObjectOperationalHoursCorrectionsThunk({
          accountId,
          objectId
        })
      ).unwrap();

      if (currentRequest !== requestId.current) return;
      setCorrections((current) => {
        const serverKeys = new Set(rows.map(correctionKey));
        const pending = current.filter(
          (row) => !serverKeys.has(correctionKey(row))
        );

        return [...pending, ...rows];
      });
    } catch {
      if (currentRequest !== requestId.current) return;
    } finally {
      if (currentRequest === requestId.current) {
        setLoading(false);
      }
    }
  }, [accountId, dispatch, objectId]);

  const addCorrection = useCallback(
    (correction: OperationalHoursCorrection) => {
      setCorrections((current) => {
        const key = correctionKey(correction);

        return [
          correction,
          ...current.filter((row) => correctionKey(row) !== key)
        ];
      });
    },
    []
  );

  useEffect(() => {
    setCorrections([]);
    refresh();

    return () => {
      requestId.current += 1;
    };
  }, [refresh]);

  return {
    corrections,
    loading,
    refresh,
    addCorrection
  };
};

const correctionKey = (correction: OperationalHoursCorrection): string =>
  [
    correction.counter,
    correction.correctedAt ?? "",
    correction.previousDisplayed,
    correction.newDisplayed
  ].join("|");
